import { usePortalAccess } from '../../components/PortalAccess';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Bar, BarChart, CartesianGrid, Legend, Line, LineChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts'
import {
  getIGDaily, getIGMonthly, getIGOverview, getIGPosts, getIGStatus, syncIG,
} from '../../utils/smmApi'
import { KPICard } from '../../components/smm/KPICard'
import { MetricTooltip } from '../../components/smm/MetricTooltip'
import { PeriodSelector } from '../../components/smm/PeriodSelector'
import { formatFull, formatNumber } from '../../utils/smmFormat'
import styles from './SmmInstagram.module.css'

const PLATFORM_COLOR = '#e1306c'
const PERIOD_OPTIONS = [
  { label: '7 дней', value: 7 },
  { label: '30 дней', value: 30 },
  { label: '90 дней', value: 90 },
]
const MONTH_OPTIONS = [
  { label: '3 мес.', value: 3 },
  { label: '6 мес.', value: 6 },
  { label: '12 мес.', value: 12 },
]
const FORMAT_OPTIONS = [
  { label: 'Все', value: 'all' },
  { label: 'Reels', value: 'reel' },
  { label: 'Фото', value: 'image' },
  { label: 'Карусели', value: 'carousel' },
  { label: 'Видео', value: 'video' },
]
const PLACEMENT_OPTIONS = [
  { label: 'Из ленты', value: 'feed' },
  { label: 'Весь контент', value: 'all' },
  { label: 'Не в ленте', value: 'not_feed' },
]
const PLACEMENT_LABELS = {
  feed: 'Лента', not_feed: 'Не в ленте', unknown: 'Неизвестно',
}
const FORMAT_LABELS = {
  reel: 'Reels', reels: 'Reels', image: 'Фото', photo: 'Фото',
  carousel: 'Карусель', carousel_album: 'Карусель', video: 'Видео', feed: 'Лента',
}

const hasValue = value => value !== null && value !== undefined
const pick = (object, keys) => {
  if (!object) return null
  for (const key of keys) {
    if (hasValue(object[key])) return object[key]
  }
  return null
}
const number = value => {
  if (!hasValue(value) || value === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}
const metric = (object, ...keys) => number(pick(object, keys))
const full = value => hasValue(value) ? formatFull(Number(value)) : '—'
const compact = value => hasValue(value) ? formatNumber(Number(value)) : '—'
const percent = value => hasValue(value) ? `${Number(value).toLocaleString('ru-RU', { maximumFractionDigits: 2 })}%` : '—'
const signed = value => hasValue(value) ? `${Number(value) > 0 ? '+' : ''}${formatFull(Number(value))}` : '—'

function formatDate(value, withTime = false) {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit', month: 'short', year: 'numeric',
    ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  }).format(date)
}

function errorText(error) {
  return error?.response?.data?.detail || error?.response?.data?.message || error?.message || 'Не удалось загрузить данные'
}

const pause = milliseconds => new Promise(resolve => setTimeout(resolve, milliseconds))
const syncState = status => String(
  status?.sync_status || status?.state || status?.last_sync_result?.status || '',
).toLowerCase()
const syncRunning = status => Boolean(
  status?.running ?? status?.sync_running ?? status?.in_progress
    ?? ['running', 'started', 'pending'].includes(syncState(status)),
)
const syncFailure = status => {
  const result = status?.last_sync_result || {}
  const failed = result.ok === false || ['error', 'failed'].includes(syncState(status))
  return failed ? (result.error || result.message || 'Meta API вернул ошибку синхронизации') : null
}

async function waitForSync(previousLastSyncAt, onStatus) {
  const deadline = Date.now() + 10 * 60 * 1000
  while (Date.now() < deadline) {
    await pause(2500)
    const nextStatus = await getIGStatus({ fresh: true })
    onStatus(nextStatus)
    const changed = Boolean(nextStatus?.last_sync_at && nextStatus.last_sync_at !== previousLastSyncAt)
    const terminal = ['success', 'completed', 'done', 'error', 'failed'].includes(syncState(nextStatus))
    if (!syncRunning(nextStatus) && (changed || terminal)) return nextStatus
  }
  throw new Error('Синхронизация выполняется дольше 10 минут. Её итог можно проверить по статусу позже.')
}

function normalizePost(post) {
  const metrics = post?.latest_metrics || post?.metrics || {}
  const merged = { ...post, ...metrics }
  const rawFormat = String(pick(post, ['format', 'media_product_type', 'media_type']) || '').toLowerCase()
  const format = rawFormat === 'clips' || rawFormat === 'reels' ? 'reel'
    : rawFormat === 'carousel_album' ? 'carousel'
      : rawFormat || 'feed'
  return {
    ...merged,
    id: post.id || post.external_id,
    external_id: post.external_id || post.id,
    published_at: post.published_at || post.timestamp,
    caption: post.caption || post.text_preview || '',
    permalink: post.permalink || post.post_url,
    format,
    placement: post.placement || 'unknown',
    isSharedToFeed: typeof post.is_shared_to_feed === 'boolean' ? post.is_shared_to_feed : null,
    reach: metric(merged, 'reach'),
    views: metric(merged, 'views', 'views_1', 'plays', 'impressions'),
    likes: metric(merged, 'likes', 'like_count'),
    comments: metric(merged, 'comments', 'comments_count'),
    saves: metric(merged, 'saves', 'saved'),
    shares: metric(merged, 'shares'),
    reposts: metric(merged, 'reposts'),
    interactions: metric(merged, 'total_interactions', 'interactions'),
    er: metric(merged, 'er'),
    erReach: metric(merged, 'er_reach', 'err'),
    erViews: metric(merged, 'er_views', 'erviews'),
    totalWatchTime: metric(merged, 'total_watch_time_ms', 'ig_reels_video_view_total_time', 'watch_time_ms'),
    avgWatchTime: metric(merged, 'avg_watch_time_ms', 'ig_reels_avg_watch_time'),
    skipRate: metric(merged, 'skip_rate', 'reels_skip_rate'),
  }
}

function normalizeFormats(rawFormats, items) {
  if (Array.isArray(rawFormats)) {
    return rawFormats.map((item) => ({
      format: String(item.format || item.name || 'feed').toLowerCase(),
      count: metric(item, 'count', 'posts_count'),
      reach: metric(item, 'reach', 'total_reach'),
      views: metric(item, 'views', 'total_views'),
      interactions: metric(item, 'interactions', 'total_interactions'),
    }))
  }
  if (rawFormats && typeof rawFormats === 'object') {
    return Object.entries(rawFormats).map(([format, item]) => ({
      format: format.toLowerCase(),
      count: typeof item === 'number' ? item : metric(item, 'count', 'posts_count'),
      reach: typeof item === 'object' ? metric(item, 'reach', 'total_reach') : null,
      views: typeof item === 'object' ? metric(item, 'views', 'total_views') : null,
      interactions: typeof item === 'object' ? metric(item, 'interactions', 'total_interactions') : null,
    }))
  }
  const grouped = new Map()
  for (const item of items) {
    const current = grouped.get(item.format) || { format: item.format, count: 0, reach: null, views: null, interactions: null }
    current.count += 1
    current.reach = hasValue(item.reach) ? (current.reach || 0) + item.reach : current.reach
    current.views = hasValue(item.views) ? (current.views || 0) + item.views : current.views
    current.interactions = hasValue(item.interactions) ? (current.interactions || 0) + item.interactions : current.interactions
    grouped.set(item.format, current)
  }
  return [...grouped.values()]
}

export default function SmmInstagram() {
  const { permissions } = usePortalAccess();
  const canManage = permissions['smm.manage'] === true;
  const [days, setDays] = useState(30)
  const [monthsCount, setMonthsCount] = useState(6)
  const [format, setFormat] = useState('all')
  const [placement, setPlacement] = useState('feed')
  const [sort, setSort] = useState('published_at')
  const [order, setOrder] = useState('desc')
  const [page, setPage] = useState(0)
  const [refreshTick, setRefreshTick] = useState(0)
  const [status, setStatus] = useState(null)
  const [overview, setOverview] = useState(null)
  const [daily, setDaily] = useState([])
  const [monthly, setMonthly] = useState(null)
  const [postsData, setPostsData] = useState({ items: [], total: 0, summary: null, formats: null })
  const [topPosts, setTopPosts] = useState([])
  const [loading, setLoading] = useState(true)
  const [postsLoading, setPostsLoading] = useState(true)
  const [syncing, setSyncing] = useState(false)
  const [error, setError] = useState('')
  const [syncMessage, setSyncMessage] = useState('')
  const monthlyRef = useRef(null)
  const pageSize = 25

  const loadDashboard = useCallback(async () => {
    setLoading(true)
    setError('')
    const results = await Promise.allSettled([
      getIGStatus(), getIGOverview(days), getIGDaily(days), getIGMonthly(monthsCount),
    ])
    const [statusResult, overviewResult, dailyResult, monthlyResult] = results
    if (statusResult.status === 'fulfilled') setStatus(statusResult.value)
    if (overviewResult.status === 'fulfilled') setOverview(overviewResult.value)
    if (dailyResult.status === 'fulfilled') {
      const payload = dailyResult.value
      setDaily(Array.isArray(payload) ? payload : payload?.items || payload?.rows || [])
    }
    if (monthlyResult.status === 'fulfilled') setMonthly(monthlyResult.value)
    const failures = results.filter(result => result.status === 'rejected')
    if (failures.length === results.length) setError(errorText(failures[0].reason))
    else if (failures.length) setError('Часть метрик пока недоступна. Остальные данные показаны ниже.')
    setLoading(false)
  }, [days, monthsCount])

  const loadPosts = useCallback(async () => {
    setPostsLoading(true)
    try {
      const [postsResult, topResult] = await Promise.allSettled([
        getIGPosts({
          days, format: format === 'all' ? undefined : format,
          placement, sort, order, limit: pageSize, offset: page * pageSize,
        }),
        getIGPosts({ days, format: 'all', placement: 'feed', sort: 'reach', order: 'desc', limit: 5, offset: 0 }),
      ])
      if (postsResult.status === 'fulfilled') {
        const payload = postsResult.value || {}
        setPostsData({
          items: Array.isArray(payload) ? payload : payload.items || [],
          total: Array.isArray(payload) ? payload.length : payload.total || 0,
          summary: payload.summary || null,
          formats: payload.formats || payload.summary?.formats || null,
        })
      } else {
        setError(errorText(postsResult.reason))
      }
      if (topResult.status === 'fulfilled') {
        const payload = topResult.value
        setTopPosts((Array.isArray(payload) ? payload : payload?.items || []).map(normalizePost))
      }
    } catch (loadError) {
      setError(errorText(loadError))
    } finally {
      setPostsLoading(false)
    }
  }, [days, format, order, page, placement, sort])

  useEffect(() => { loadDashboard() }, [loadDashboard, refreshTick])
  useEffect(() => { loadPosts() }, [loadPosts, refreshTick])
  useEffect(() => { setPage(0) }, [days, format, placement])

  useEffect(() => {
    if (!monthly?.months?.length) return undefined
    const frame = requestAnimationFrame(() => {
      if (monthlyRef.current) monthlyRef.current.scrollLeft = monthlyRef.current.scrollWidth
    })
    return () => cancelAnimationFrame(frame)
  }, [monthly])

  const handleSync = async () => {
    setSyncing(true)
    setSyncMessage('Синхронизация запущена…')
    setError('')
    try {
      const previousLastSyncAt = status?.last_sync_at || overview?.last_sync_at || null
      const result = await syncIG()
      const startedInBackground = ['started', 'running', 'pending', 'already_running'].includes(String(result?.status || '').toLowerCase())
      const finalStatus = startedInBackground
        ? await waitForSync(previousLastSyncAt, setStatus)
        : await getIGStatus({ fresh: true }).catch(() => status)
      const failure = syncFailure(finalStatus)
      if (failure) throw new Error(failure)
      const syncResult = finalStatus?.last_sync_result || result || {}
      const count = pick(syncResult, ['synced_posts', 'posts_synced', 'media_synced'])
      setSyncMessage(hasValue(count) ? `Синхронизировано объектов контента: ${formatFull(Number(count))}` : 'Синхронизация завершена')
      setRefreshTick(value => value + 1)
    } catch (syncError) {
      setError(`Синхронизация не выполнена: ${errorText(syncError)}`)
    } finally {
      setSyncing(false)
    }
  }

  const account = overview?.account || {}
  const kpi = overview?.kpi || overview?.metrics || overview || {}
  const lastSyncFailure = syncFailure(status)
  const accountMetric = (...keys) => metric(account, ...keys)
  const kpiMetric = (...keys) => metric(kpi, ...keys)
  const normalizedPosts = useMemo(() => (postsData.items || []).map(normalizePost), [postsData.items])
  const formats = useMemo(
    () => normalizeFormats(postsData.formats, normalizedPosts),
    [postsData.formats, normalizedPosts],
  )
  const totalPages = Math.max(1, Math.ceil((postsData.total || 0) / pageSize))
  const netFollows = kpiMetric('net_follows', 'followers_change', 'subscriber_change')
    ?? (() => {
      const follows = kpiMetric('follows', 'subscribed')
      const unfollows = kpiMetric('unfollows', 'unsubscribed')
      return hasValue(follows) && hasValue(unfollows) ? follows - unfollows : null
    })()
  const availableMetrics = overview?.available_metrics || []
  const unavailableMetrics = overview?.unavailable_metrics || []
  const monthlyChartData = (monthly?.items || []).map(item => ({
    label: item.label,
    followers: metric(item.account, 'followers_count'),
    net_follows: metric(item.kpi, 'net_follows'),
    account_reach: metric(item.kpi, 'account_reach'),
    post_reach: metric(item.kpi, 'post_reach', 'total_post_reach'),
    interactions: metric(item.kpi, 'content_interactions', 'total_interactions'),
  }))

  if (loading && !overview) {
    return <div className={styles.loading}><span className="spinner" /></div>
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.identity}>
          <span className={styles.platformDot} />
          <div>
            <h1 className={styles.title}>Instagram</h1>
            <p className={styles.subtitle}>
              {account.username ? `@${String(account.username).replace(/^@/, '')} · ` : ''}
              автоматическая статистика Meta API
            </p>
          </div>
        </div>
        <div className={styles.headerActions}>
          <PeriodSelector options={PERIOD_OPTIONS} value={days} onChange={setDays} />
          {canManage && <button className="btn btn-primary btn-sm" onClick={handleSync} disabled={syncing || status?.configured === false}>
            {syncing ? 'Синхронизация…' : 'Синхронизировать'}
          </button>}
        </div>
      </header>

      <div className={styles.statusRow}>
        <span className={`${styles.statusBadge} ${status?.configured === false || lastSyncFailure ? styles.statusError : styles.statusOk}`}>
          {status?.configured === false ? 'API не настроен' : lastSyncFailure ? 'Ошибка синхронизации' : 'API подключён'}
        </span>
        <span>Последняя синхронизация: {formatDate(status?.last_sync_at || overview?.last_sync_at, true)}</span>
        {syncMessage && <span className={styles.successText}>{syncMessage}</span>}
      </div>

      {status?.configured === false && (
        <div className={styles.notice}>
          Instagram API ещё не настроен на сервере. Токен и ID аккаунта задаются только в защищённых переменных окружения — на портале они не отображаются.
        </div>
      )}
      {lastSyncFailure && !error && (
        <div className={styles.errorNotice}>Последняя синхронизация не выполнена: {lastSyncFailure}</div>
      )}
      {error && <div className={styles.errorNotice}>{error}</div>}

      <section className={styles.section}>
        <SectionTitle title="Аккаунт" hint={`за последние ${days} дней`} />
        <div className={styles.kpiGrid}>
          <KPICard label="Подписчики" value={full(accountMetric('followers_count', 'followers', 'subscribers') ?? kpiMetric('followers_count', 'followers', 'subscribers'))}
            tooltip="Текущее количество подписчиков профессионального аккаунта" source="Instagram Graph API" />
          <KPICard label="Подписались" value={full(kpiMetric('follows', 'subscribed'))}
            tooltip="Новые подписки за выбранный период" source="Instagram Graph API" />
          <KPICard label="Отписались" value={full(kpiMetric('unfollows', 'unsubscribed'))}
            tooltip="Отписки за выбранный период" source="Instagram Graph API" />
          <KPICard label="Чистый прирост" value={signed(netFollows)}
            color={hasValue(netFollows) ? (netFollows >= 0 ? 'var(--success)' : 'var(--danger)') : undefined}
            tooltip="Подписались минус отписались" source="Расчёт" />
          <KPICard label="Сумма дневного охвата" value={compact(kpiMetric('account_reach', 'reach'))}
            tooltip="Сумма дневных значений охвата; один аккаунт может учитываться в разные дни" source="Instagram Graph API" />
          <KPICard label="Просмотры контента" value={compact(kpiMetric('account_views', 'content_views', 'views'))}
            tooltip="Все просмотры контента аккаунта за период" source="Instagram Graph API" />
          <KPICard label="Посещения профиля" value={compact(kpiMetric('profile_views'))}
            tooltip="Количество посещений профиля" source="Instagram Graph API" />
          <KPICard label="Вовлечённые аккаунты" value={compact(kpiMetric('accounts_engaged', 'engaged_accounts'))}
            tooltip="Уникальные аккаунты, взаимодействовавшие с контентом" source="Instagram Graph API" />
          <KPICard label="Взаимодействия" value={compact(kpiMetric('total_interactions', 'interactions'))}
            tooltip="Лайки, комментарии, сохранения и отправки" source="Instagram Graph API" />
          <KPICard label="Переходы по ссылкам" value={compact(kpiMetric('profile_links_taps', 'bio_link_clicks', 'link_clicks'))}
            tooltip="Нажатия на ссылки в профиле" source="Instagram Graph API" />
        </div>
      </section>

      <section className={styles.section}>
        <SectionTitle title="Публикации" hint={`счётчик — основная лента; показатели — весь контент за ${days} дней`} />
        <div className={styles.kpiGrid}>
          <KPICard label="Публикации" value={full(kpiMetric('posts_count', 'media_count_period'))}
            tooltip="Фото, карусели, видео и Reels, опубликованные в основной ленте. Reels не из ленты, включая пробные, и Stories не учитываются." source="Instagram Graph API" />
          <KPICard label="Охват публикаций" value={compact(kpiMetric('post_reach', 'total_post_reach', 'media_reach'))}
            tooltip="Сумма охватов всего синхронизированного контента, включая Reels не из ленты и пробные Reels." source="Instagram Graph API" />
          <KPICard label="Просмотры публикаций" value={compact(kpiMetric('post_views', 'total_post_views', 'media_views'))}
            tooltip="Сумма просмотров всего синхронизированного контента, включая Reels не из ленты и пробные Reels." source="Instagram Graph API" />
          <KPICard label="Средний охват" value={compact(kpiMetric('avg_post_reach', 'avg_reach'))}
            tooltip="Средний охват по всему синхронизированному контенту, включая Reels не из ленты и пробные Reels." source="Расчёт" />
          <KPICard label="Средние просмотры" value={compact(kpiMetric('avg_post_views', 'avg_views'))}
            tooltip="Среднее число просмотров по всему синхронизированному контенту, включая Reels не из ленты и пробные Reels." source="Расчёт" />
          <KPICard label="Лайки" value={compact(kpiMetric('likes', 'total_likes'))}
            tooltip="Лайки всего синхронизированного контента, включая Reels не из ленты и пробные Reels." source="Instagram Graph API" />
          <KPICard label="Комментарии" value={compact(kpiMetric('comments', 'total_comments'))}
            tooltip="Комментарии ко всему синхронизированному контенту, включая Reels не из ленты и пробные Reels." source="Instagram Graph API" />
          <KPICard label="Сохранения" value={compact(kpiMetric('saves', 'total_saves'))}
            tooltip="Сохранения всего синхронизированного контента, включая Reels не из ленты и пробные Reels." source="Instagram Graph API" />
          <KPICard label="Отправки" value={compact(kpiMetric('shares', 'total_shares', 'total_reposts'))}
            tooltip="Отправки всего синхронизированного контента, включая Reels не из ленты и пробные Reels." source="Instagram Graph API" />
          <KPICard label="ER" value={percent(kpiMetric('er'))}
            tooltip="Взаимодействия / подписчики × 100" source="Расчёт" />
          <KPICard label="ERReach" value={percent(kpiMetric('er_reach', 'err'))}
            tooltip="Взаимодействия / охват публикаций × 100" source="Расчёт" />
          <KPICard label="ERViews" value={percent(kpiMetric('er_views', 'erviews'))}
            tooltip="Взаимодействия / просмотры публикаций × 100" source="Расчёт" />
        </div>
      </section>

      <div className={styles.chartsGrid}>
        <ChartCard title="Охват и просмотры" hint="по дням">
          {daily.length ? (
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={daily} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fontSize: 10, fill: 'var(--text-muted)' }} />
                <YAxis width={42} tick={{ fontSize: 10, fill: 'var(--text-muted)' }} tickFormatter={formatNumber} />
                <Tooltip content={<ChartTooltip />} />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="reach" stroke={PLATFORM_COLOR} dot={false} strokeWidth={2} name="Охват" connectNulls />
                <Line type="monotone" dataKey="views" stroke="#8b5cf6" dot={false} strokeWidth={2} name="Просмотры" connectNulls />
                <Line type="monotone" dataKey="profile_views" stroke="#f59e0b" dot={false} strokeWidth={1.5} name="Профиль" connectNulls />
              </LineChart>
            </ResponsiveContainer>
          ) : <EmptyChart />}
        </ChartCard>

        <ChartCard title="Динамика подписок" hint="по дням">
          {daily.length ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={daily} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fontSize: 10, fill: 'var(--text-muted)' }} />
                <YAxis width={34} tick={{ fontSize: 10, fill: 'var(--text-muted)' }} />
                <Tooltip content={<ChartTooltip />} />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="follows" fill="#22c55e" radius={[3, 3, 0, 0]} name="Подписались" />
                <Bar dataKey="unfollows" fill="#ef4444" radius={[3, 3, 0, 0]} name="Отписались" />
              </BarChart>
            </ResponsiveContainer>
          ) : <EmptyChart />}
        </ChartCard>
      </div>

      {formats.length > 0 && (
        <section className={styles.section}>
          <SectionTitle title="Форматы контента" hint="количество — только основная лента; охват, просмотры и взаимодействия — весь контент; Stories не включены" />
          <div className={styles.formatGrid}>
            {formats.map(item => (
              <div className={styles.formatCard} key={item.format}>
                <div className={styles.formatTitle}>{FORMAT_LABELS[item.format] || item.format}</div>
                <div className={styles.formatCount}>{full(item.count)} <span>публ.</span></div>
                <dl>
                  <div><dt>Охват</dt><dd>{compact(item.reach)}</dd></div>
                  <div><dt>Просмотры</dt><dd>{compact(item.views)}</dd></div>
                  <div><dt>Взаимодействия</dt><dd>{compact(item.interactions)}</dd></div>
                </dl>
              </div>
            ))}
          </div>
        </section>
      )}

      {topPosts.length > 0 && (
        <section className={styles.section}>
          <SectionTitle title="Лучшие публикации" hint={`по охвату за ${days} дней · только основная лента`} />
          <div className={styles.topGrid}>
            {topPosts.map((post, index) => (
              <a key={post.external_id} className={styles.topCard} href={post.permalink || undefined}
                target={post.permalink ? '_blank' : undefined} rel={post.permalink ? 'noopener noreferrer' : undefined}>
                <span className={styles.topRank}>#{index + 1}</span>
                <div className={styles.topBody}>
                  <span className={styles.topMeta}>{formatDate(post.published_at)} · {FORMAT_LABELS[post.format] || post.format}</span>
                  <strong>{post.caption || '(без подписи)'}</strong>
                  <span>Охват {compact(post.reach)} · Просмотры {compact(post.views)} · Взаимодействия {compact(post.interactions)}</span>
                </div>
              </a>
            ))}
          </div>
        </section>
      )}

      {monthly?.months?.length > 0 && monthly?.rows?.length > 0 && (
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <SectionTitle title="Сравнение по месяцам" hint="аккаунт — за месяц; публикации — текущий lifetime-срез по месяцу выхода" />
            <PeriodSelector options={MONTH_OPTIONS} value={monthsCount} onChange={setMonthsCount} />
          </div>
          {monthlyChartData.length > 0 && (
            <div className={styles.chartsGrid}>
              <ChartCard title="Подписчики по месяцам" hint="всего и чистый прирост">
                <ResponsiveContainer width="100%" height={230}>
                  <LineChart data={monthlyChartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis dataKey="label" tick={{ fontSize: 10, fill: 'var(--text-muted)' }} />
                    <YAxis yAxisId="followers" width={44} tick={{ fontSize: 10, fill: 'var(--text-muted)' }} tickFormatter={formatNumber} />
                    <YAxis yAxisId="growth" orientation="right" width={34} tick={{ fontSize: 10, fill: 'var(--text-muted)' }} />
                    <Tooltip content={<ChartTooltip />} />
                    <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                    <Line yAxisId="followers" type="monotone" dataKey="followers" stroke={PLATFORM_COLOR} dot={{ r: 3 }} strokeWidth={2} name="Подписчики" connectNulls />
                    <Line yAxisId="growth" type="monotone" dataKey="net_follows" stroke="#22c55e" dot={{ r: 2 }} strokeWidth={1.5} name="Чистый прирост" connectNulls />
                  </LineChart>
                </ResponsiveContainer>
              </ChartCard>
              <ChartCard title="Результат по месяцам" hint="охваты и взаимодействия">
                <ResponsiveContainer width="100%" height={230}>
                  <BarChart data={monthlyChartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis dataKey="label" tick={{ fontSize: 10, fill: 'var(--text-muted)' }} />
                    <YAxis width={42} tick={{ fontSize: 10, fill: 'var(--text-muted)' }} tickFormatter={formatNumber} />
                    <Tooltip content={<ChartTooltip />} />
                    <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="account_reach" fill="#e1306c" radius={[3, 3, 0, 0]} name="Сумма дневного охвата" />
                    <Bar dataKey="post_reach" fill="#8b5cf6" radius={[3, 3, 0, 0]} name="Охват публикаций" />
                    <Bar dataKey="interactions" fill="#f59e0b" radius={[3, 3, 0, 0]} name="Взаимодействия" />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </div>
          )}
          <div ref={monthlyRef} className={`${styles.tableWrapper} ${styles.monthlyTable}`}>
            <table className={styles.table}>
              <thead><tr><th>Метрика</th>{monthly.months.map(month => <th key={`${month.year}-${month.month}`}>{month.label}</th>)}</tr></thead>
              <tbody>
                {monthly.rows.map(row => (
                  <tr key={row.label}>
                    <td className={styles.monthlyMetric}>{row.label}</td>
                    {(row.values || []).map((value, index) => (
                      <td key={index}>{!hasValue(value) ? '—' : row.fmt === 'percent' ? percent(value) : row.fmt === 'full' ? full(value) : compact(value)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {(availableMetrics.length > 0 || unavailableMetrics.length > 0) && (
        <details className={styles.capabilities}>
          <summary>Доступность метрик Meta API</summary>
          <div className={styles.capabilityGrid}>
            <div><strong>Получены</strong><p>{availableMetrics.length ? availableMetrics.join(', ') : '—'}</p></div>
            <div><strong>Недоступны для текущего аккаунта/контента</strong><p>{unavailableMetrics.length ? unavailableMetrics.join(', ') : '—'}</p></div>
          </div>
        </details>
      )}

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <SectionTitle
            title="Контент"
            hint={`${postsData.total || 0} публикаций за период · ${PLACEMENT_OPTIONS.find(option => option.value === placement)?.label || 'выбранный фильтр'}`}
          />
          <div className={styles.filterStack}>
            <div className={styles.filterGroup}>
              <span className={styles.filterLabel}>Размещение</span>
              <div className={styles.filters}>
                {PLACEMENT_OPTIONS.map(option => (
                  <button
                    key={option.value}
                    type="button"
                    aria-pressed={placement === option.value}
                    className={`btn btn-sm ${placement === option.value ? 'btn-primary' : 'btn-ghost'}`}
                    onClick={() => setPlacement(option.value)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
            <div className={styles.filterGroup}>
              <span className={styles.filterLabel}>Формат</span>
              <div className={styles.filters}>
                {FORMAT_OPTIONS.map(option => (
                  <button
                    key={option.value}
                    type="button"
                    aria-pressed={format === option.value}
                    className={`btn btn-sm ${format === option.value ? 'btn-primary' : 'btn-ghost'}`}
                    onClick={() => setFormat(option.value)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
        <p className={styles.contentScopeNote}>
          По умолчанию показана только основная лента. Meta API не помечает Trial Reel отдельным полем:
          фильтр «Не в ленте» показывает Reels, не добавленные в основную ленту, включая пробные.
        </p>
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <SortHeader column="published_at" label="Публикация" description="Дата выхода и подпись публикации из Instagram." sort={sort} order={order} onChange={change => { setSort(change.sort); setOrder(change.order) }} />
                <TableHeader label="Формат" description="Тип публикации: Reel, фото, карусель или видео." />
                <TableHeader label="Размещение" description="«Лента» — публикация видна в основной ленте. «Не в ленте» — Reel опубликован без добавления в основную ленту; сюда входят пробные Reels." />
                <SortHeader column="reach" label="Охват" description="Количество уникальных аккаунтов, которым была показана публикация." sort={sort} order={order} onChange={change => { setSort(change.sort); setOrder(change.order) }} />
                <SortHeader column="views" label="Просмотры" description="Количество просмотров или воспроизведений публикации, включая повторные." sort={sort} order={order} onChange={change => { setSort(change.sort); setOrder(change.order) }} />
                <SortHeader column="likes" label="Лайки" description="Количество отметок «Нравится» у публикации." sort={sort} order={order} onChange={change => { setSort(change.sort); setOrder(change.order) }} />
                <SortHeader column="comments" label="Комментарии" description="Количество комментариев к публикации." sort={sort} order={order} onChange={change => { setSort(change.sort); setOrder(change.order) }} />
                <SortHeader column="saves" label="Сохранения" description="Количество сохранений публикации пользователями." sort={sort} order={order} onChange={change => { setSort(change.sort); setOrder(change.order) }} />
                <SortHeader column="shares" label="Отправки" description="Количество отправок публикации другим пользователям." sort={sort} order={order} onChange={change => { setSort(change.sort); setOrder(change.order) }} />
                <SortHeader column="er" label="ER" description="Взаимодействия с публикацией / текущее число подписчиков × 100." source="Расчёт" sort={sort} order={order} onChange={change => { setSort(change.sort); setOrder(change.order) }} />
                <SortHeader column="er_reach" label="ERReach" description="Взаимодействия с публикацией / её охват × 100." source="Расчёт" sort={sort} order={order} onChange={change => { setSort(change.sort); setOrder(change.order) }} />
                <SortHeader column="er_views" label="ERViews" description="Взаимодействия с публикацией / её просмотры × 100." source="Расчёт" sort={sort} order={order} onChange={change => { setSort(change.sort); setOrder(change.order) }} />
              </tr>
            </thead>
            <tbody>
              {normalizedPosts.map(post => (
                <tr key={post.external_id}>
                  <td className={styles.postCell}>
                    <span className={styles.postDate}>{formatDate(post.published_at)}</span>
                    {post.permalink ? <a href={post.permalink} target="_blank" rel="noopener noreferrer">{post.caption || '(без подписи)'}</a>
                      : <span>{post.caption || '(без подписи)'}</span>}
                  </td>
                  <td><span className={`${styles.formatBadge} ${styles[`format_${post.format}`] || ''}`}>{FORMAT_LABELS[post.format] || post.format}</span></td>
                  <td><span className={`${styles.placementBadge} ${styles[`placement_${post.placement}`] || ''}`}>{PLACEMENT_LABELS[post.placement] || post.placement}</span></td>
                  <td>{compact(post.reach)}</td><td>{compact(post.views)}</td><td>{compact(post.likes)}</td>
                  <td>{compact(post.comments)}</td><td>{compact(post.saves)}</td><td>{compact(post.shares)}</td>
                  <td>{percent(post.er)}</td><td>{percent(post.erReach)}</td><td>{percent(post.erViews)}</td>
                </tr>
              ))}
              {!postsLoading && normalizedPosts.length === 0 && (
                <tr><td colSpan={12} className={styles.emptyTable}>Нет контента для выбранных фильтров и периода</td></tr>
              )}
            </tbody>
          </table>
          {postsLoading && <div className={styles.tableLoader}><span className="spinner" /></div>}
        </div>
        {totalPages > 1 && (
          <div className={styles.pagination}>
            <button className="btn btn-secondary btn-sm" disabled={page === 0} onClick={() => setPage(value => value - 1)}>←</button>
            <span>{page + 1} / {totalPages}</span>
            <button className="btn btn-secondary btn-sm" disabled={page + 1 >= totalPages} onClick={() => setPage(value => value + 1)}>→</button>
          </div>
        )}
      </section>
    </div>
  )
}

function SectionTitle({ title, hint }) {
  return <div><h2 className={styles.sectionTitle}>{title}</h2>{hint && <p className={styles.sectionHint}>{hint}</p>}</div>
}

function ChartCard({ title, hint, children }) {
  return (
    <div className={styles.chartCard}>
      <div className={styles.chartHeader}><span>{title}</span><small>{hint}</small></div>
      {children}
    </div>
  )
}

function EmptyChart() {
  return <div className={styles.emptyChart}>Данные появятся после синхронизации</div>
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null
  return (
    <div className={styles.chartTooltip}>
      <strong>{formatDate(label)}</strong>
      {payload.map(item => <span key={item.dataKey} style={{ color: item.color }}>{item.name}: {compact(item.value)}</span>)}
    </div>
  )
}

function TableHeader({ label, description, source = 'API' }) {
  return (
    <th>
      <span className={styles.tableHeaderLabel}>
        {label}
        <MetricTooltip description={description} source={source} />
      </span>
    </th>
  )
}

function SortHeader({ column, label, description, source = 'API', sort, order, onChange }) {
  const active = sort === column
  const nextOrder = active && order === 'desc' ? 'asc' : 'desc'
  return (
    <th className={styles.sortHeader} aria-sort={active ? (order === 'desc' ? 'descending' : 'ascending') : 'none'}>
      <span className={styles.tableHeaderLabel}>
        <button type="button" className={styles.sortButton} onClick={() => onChange({ sort: column, order: nextOrder })}>
          {label} <span>{active ? (order === 'desc' ? '↓' : '↑') : '↕'}</span>
        </button>
        <MetricTooltip description={description} source={source} />
      </span>
    </th>
  )
}

function shortDate(value) {
  if (!value) return ''
  const parts = String(value).split('-')
  return parts.length === 3 ? `${parts[2]}.${parts[1]}` : value
}
